import { useState, useMemo, useRef, useCallback } from "react";
import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  CalendarDays,
  Download,
  Printer,
  FileText,
  DollarSign,
  Sparkles,
  ClipboardList,
  TrendingUp,
  Users,
  Home,
  Filter,
  BarChart3,
  Eye,
  HardHat,
  Banknote,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Loader2,
  Trash2,
  Receipt,
} from "lucide-react";
import { useApartments, useAdminUsers } from "@/hooks/use-resources";
import { DataGrid, KpiCard, MetricStrip, type DateRangeValue } from "@/components/dashboard";
import { formatCurrency } from "@/lib/format";
import type { ColumnDef } from "@tanstack/react-table";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import OperationsTab from "./admin-reports/operations-tab";
import RevenueTab from "./admin-reports/revenue-tab";
import SalesTab from "./admin-reports/sales-tab";
import type { SalesReportResponse } from "@shared/reports-sales";
import AiInsightsTab from "./admin-reports/ai-insights-tab";
import ReportsFilterBar from "./admin-reports/reports-filter-bar";

type SortDirection = "asc" | "desc" | null;
type SortConfig = { key: string; direction: SortDirection };

// Response shapes for the operations and revenue report endpoints (BUG-T13)
type WorkerSummaryRow = {
  userId: number;
  name: string;
  jobs: number;
  pendingJobs: number;
  completedJobs: number;
  totalPayout: string;
  pendingPayout: string;
  estimatedPayout: string;
};

type OpsReportData = {
  cleaningJobs: any[];
  maintenanceJobs: any[];
  workerSummary: WorkerSummaryRow[];
  summary: {
    totalCleaningJobs: number;
    completedCleaningJobs: number;
    pendingCleaningJobs: number;
    totalMaintenanceJobs: number;
    completedMaintenanceJobs: number;
    totalCleaningRevenue: string;
    totalMaintenanceCost: string;
    totalOperationsCost?: string;
  };
};

type RevReportData = {
  reservations: any[];
  financials: any[];
  commissions: any[];
  summary: {
    totalReservations: number;
    activeReservations: number;
    totalRevenue: string;
    totalCommissions: string;
    totalPaid: string;
    totalPending: string;
    bySource: Record<string, { count: number; revenue: number }>;
  };
};

function useSortableData<T>(items: T[], defaultSort?: SortConfig) {
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSort || { key: "", direction: null });

  const sortedItems = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction || !items?.length) return items || [];
    const sorted = [...items].sort((a: any, b: any) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, sortConfig]);

  const requestSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" as SortDirection };
        if (prev.direction === "desc") return { key: "", direction: null };
        return { key, direction: "asc" as SortDirection };
      }
      return { key, direction: "asc" as SortDirection };
    });
  }, []);

  return { sortedItems, sortConfig, requestSort };
}

function SortableHead({
  label,
  sortKey,
  sortConfig,
  onSort,
  className = "",
}: {
  label: string;
  sortKey: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  className?: string;
}) {
  const isActive = sortConfig.key === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        className="flex items-center gap-1 hover-elevate rounded-md px-1 py-0.5 -mx-1 text-left whitespace-nowrap"
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
      >
        {label}
        {isActive && sortConfig.direction === "asc" ? (
          <ArrowUp className="w-3 h-3 text-foreground" />
        ) : isActive && sortConfig.direction === "desc" ? (
          <ArrowDown className="w-3 h-3 text-foreground" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
        )}
      </button>
    </TableHead>
  );
}

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateShort(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** 'YYYY-MM-DD' from the picker's LOCAL calendar day. toISOString() would shift an
 *  end-of-day boundary (23:59:59 local) into the next UTC day for US timezones. */
function toLocalDateStr(d: Date | null | undefined): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

function statusColor(status: string) {
  switch (status) {
    case "confirmed": return "secondary";
    case "checked_in": return "default";
    case "checked_out": return "outline";
    case "cancelled": return "destructive";
    case "completed": return "default";
    case "in_progress": return "secondary";
    case "pending": return "outline";
    case "urgent": return "destructive";
    case "paid": return "default";
    default: return "outline";
  }
}

function defaultThisMonth(): DateRangeValue {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
  };
}

export default function AdminReports() {
  const [dateRange, setDateRange] = useState<DateRangeValue>(defaultThisMonth);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [apartmentFilter, setApartmentFilter] = useState("all");
  const [cleanerFilter, setCleanerFilter] = useState("all");
  const [opsStatusFilter, setOpsStatusFilter] = useState("all");
  const [ownerView, setOwnerView] = useState(false);
  const [cleanerView, setCleanerView] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const repairMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/repair-cleaner-records'),
    onSuccess: async (res) => {
      const data = await res.json();
      toast({ title: "Sync Complete", description: data.message });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reports'] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to sync cleaner records", variant: "destructive" });
    },
  });

  const cleanupMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/cleanup-duplicate-cleaning-records'),
    onSuccess: async (res) => {
      const data = await res.json();
      toast({ title: "Cleanup Complete", description: data.message });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/reports'] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to clean up records", variant: "destructive" });
    },
  });

  const { data: apartmentsData } = useApartments();
  const { data: usersData } = useAdminUsers();
  const apartments = apartmentsData || [];
  const allUsers = usersData || [];
  const owners = allUsers.filter((u: any) => u.role === "owner");
  const cleaners = allUsers.filter((u: any) => u.role === "cleaner" || ((u.additionalRoles as string[] | undefined) ?? []).includes("cleaner"));

  const startStr = toLocalDateStr(dateRange.from);
  const endStr = toLocalDateStr(dateRange.to);

  const reportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (startStr) params.set("startDate", startStr);
    if (endStr) params.set("endDate", endStr);
    if (ownerFilter !== "all") params.set("ownerUserId", ownerFilter);
    if (apartmentFilter !== "all") params.set("apartmentId", apartmentFilter);
    if (cleanerFilter !== "all") params.set("cleanerUserId", cleanerFilter);
    return `/api/admin/reports?${params.toString()}`;
  }, [startStr, endStr, ownerFilter, apartmentFilter, cleanerFilter]);

  const { data: reportData, isLoading } = useQuery({
    queryKey: [reportUrl],
    enabled: !!startStr && !!endStr,
  });

  const opsReportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (startStr) params.set("startDate", startStr);
    if (endStr) params.set("endDate", endStr);
    if (apartmentFilter !== "all") params.set("apartmentId", apartmentFilter);
    if (cleanerFilter !== "all") params.set("cleanerUserId", cleanerFilter);
    if (opsStatusFilter !== "all") params.set("statusFilter", opsStatusFilter);
    return `/api/admin/reports/operations?${params.toString()}`;
  }, [startStr, endStr, apartmentFilter, cleanerFilter, opsStatusFilter]);

  const revReportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (startStr) params.set("startDate", startStr);
    if (endStr) params.set("endDate", endStr);
    if (apartmentFilter !== "all") params.set("apartmentId", apartmentFilter);
    if (ownerFilter !== "all") params.set("ownerUserId", ownerFilter);
    return `/api/admin/reports/revenue?${params.toString()}`;
  }, [startStr, endStr, apartmentFilter, ownerFilter]);

  const salesReportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (startStr) params.set("startDate", startStr);
    if (endStr) params.set("endDate", endStr);
    if (apartmentFilter !== "all") params.set("apartmentId", apartmentFilter);
    if (ownerFilter !== "all") params.set("ownerUserId", ownerFilter);
    return `/api/admin/reports/sales?${params.toString()}`;
  }, [startStr, endStr, apartmentFilter, ownerFilter]);

  const { data: opsData } = useQuery<OpsReportData>({ queryKey: [opsReportUrl], enabled: !!startStr && !!endStr });
  const { data: revData } = useQuery<RevReportData>({ queryKey: [revReportUrl], enabled: !!startStr && !!endStr });
  const { data: salesData, isLoading: salesLoading } = useQuery<SalesReportResponse>({ queryKey: [salesReportUrl], enabled: !!startStr && !!endStr });

  function generatePDF() {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let yPos = 16;

    const primaryColor: [number, number, number] = [194, 101, 42];
    const accentColor: [number, number, number] = [180, 130, 60];
    const headerBg: [number, number, number] = [252, 247, 242];
    const darkText: [number, number, number] = [30, 30, 30];
    const mutedText: [number, number, number] = [120, 120, 120];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const title = ownerView ? "Property Manager Report" : cleanerView ? "Cleaner Earnings Report" : "Staio Report";
    doc.text("STAIO", pageWidth - margin - 20, 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Property Management", pageWidth - margin - 35, 18);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(title, margin, yPos);
    yPos += 7;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const periodText = dateRange.from && dateRange.to
      ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
      : "All dates";
    let subtitle = periodText;
    if (ownerFilter !== "all") subtitle += ` | PM: ${userMap[Number(ownerFilter)] || ownerFilter}`;
    if (apartmentFilter !== "all") subtitle += ` | Apt: ${apartmentMap[Number(apartmentFilter)] || apartmentFilter}`;
    if (cleanerFilter !== "all") subtitle += ` | Cleaner: ${userMap[Number(cleanerFilter)] || cleanerFilter}`;
    doc.text(subtitle, margin, yPos);
    yPos = 36;

    const addSectionTitle = (text: string) => {
      if (yPos > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        yPos = 16;
      }
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkText);
      doc.text(text, margin, yPos);
      yPos += 2;
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 6;
    };

    const addKpiRow = (items: { label: string; value: string }[]) => {
      if (yPos > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        yPos = 16;
      }
      const boxWidth = (pageWidth - margin * 2 - (items.length - 1) * 4) / items.length;
      items.forEach((item, i) => {
        const x = margin + i * (boxWidth + 4);
        doc.setFillColor(...headerBg);
        doc.roundedRect(x, yPos, boxWidth, 16, 2, 2, "F");
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...darkText);
        doc.text(item.value, x + boxWidth / 2, yPos + 7, { align: "center" });
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...mutedText);
        doc.text(item.label, x + boxWidth / 2, yPos + 13, { align: "center" });
      });
      yPos += 22;
    };

    const addTable = (head: string[][], body: string[][], columnStyles?: Record<number, { halign?: "left" | "center" | "right" }>) => {
      autoTable(doc, {
        startY: yPos,
        head,
        body,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2.5, textColor: darkText, lineColor: [220, 220, 220], lineWidth: 0.1 },
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
        alternateRowStyles: { fillColor: [250, 250, 252] },
        columnStyles: columnStyles || {},
        didDrawPage: () => {},
      });
      yPos = (doc as any).lastAutoTable.finalY + 8;
    };

    addSectionTitle("Summary");

    const kpis: { label: string; value: string }[] = [];
    if (!cleanerView) {
      kpis.push({ label: "Reservations", value: String(resData?.summary?.total || 0) });
    }
    kpis.push({ label: "Cleaning Jobs", value: String(cleanData?.summary?.total || 0) });
    kpis.push({
      label: cleanerView ? "Total Earnings" : ownerView ? "Total Charges" : "Total Revenue",
      value: cleanerView ? formatMoney(finData?.summary?.totalCleanerPayments) : formatMoney(finData?.summary?.totalRevenue),
    });
    if (!cleanerView) {
      kpis.push({ label: "Guest Checkouts", value: String(subData?.summary?.total || 0) });
    }
    addKpiRow(kpis);

    if (!cleanerView) {
      const breakdownItems: [string, string][] = [];
      if (!cleanerView) {
        breakdownItems.push(["Check-in Fees", formatMoney(finData?.summary?.checkInFees)]);
        breakdownItems.push(["Cleaning Fees", formatMoney(finData?.summary?.cleaningFees)]);
        breakdownItems.push(["Unit Setup Fees", formatMoney(finData?.summary?.unitSetupFees)]);
        breakdownItems.push(["Maintenance", formatMoney(finData?.summary?.maintenanceFees)]);
        breakdownItems.push(["Miscellaneous", formatMoney(finData?.summary?.miscFees)]);
      }
      if (!ownerView && !cleanerView) {
        breakdownItems.push(["Admin Commission", formatMoney(finData?.summary?.totalAdminCommission)]);
      }
      breakdownItems.push(["Cleaner Payments", formatMoney(finData?.summary?.totalCleanerPayments)]);
      if (!ownerView && !cleanerView) {
        breakdownItems.push([`${finData?.summary?.partnerName || "Partner"} (${finData?.summary?.partnerCommissionPct || "14"}%)`, formatMoney(finData?.summary?.totalPartnerPayments)]);
      }
      breakdownItems.push(["Paid Amount", formatMoney(finData?.summary?.paidAmount)]);
      breakdownItems.push(["Pending Amount", formatMoney(finData?.summary?.pendingAmount)]);

      addSectionTitle("Financial Breakdown");
      addTable(
        [["Category", "Amount"]],
        breakdownItems,
        { 1: { halign: "right" } }
      );
    }

    if (!cleanerView) {
      const resRecords = resSorter.sortedItems || [];
      if (resRecords.length > 0) {
        addSectionTitle("Reservations");
        addKpiRow([
          { label: "Confirmed", value: String(resData?.summary?.confirmed || 0) },
          { label: "Checked In", value: String(resData?.summary?.checkedIn || 0) },
          { label: "Checked Out", value: String(resData?.summary?.checkedOut || 0) },
          { label: "Cancelled", value: String(resData?.summary?.cancelled || 0) },
        ]);
        const resBody = resRecords.map((r: any) => [
          r.guestName || "-",
          r.apartmentName || apartmentMap[r.apartmentId] || "-",
          r.checkInDate ? formatDateShort(r.checkInDate) : "-",
          r.checkOutDate ? formatDateShort(r.checkOutDate) : "-",
          r.status || "-",
          formatMoney(r.totalCharges),
        ]);
        addTable(
          [["Guest", "Apartment", "Check-in", "Check-out", "Status", "Charges"]],
          resBody,
          { 5: { halign: "right" } }
        );
      }
    }

    const cleanRecords = cleanSorter.sortedItems || [];
    if (cleanRecords.length > 0) {
      addSectionTitle("Cleaning Jobs");
      const cleanBody = cleanRecords.map((c: any) => [
        c.apartmentName || apartmentMap[c.apartmentId] || "-",
        c.assignedToName || userMap[c.assignedToUserId] || "-",
        c.status || "-",
        c.priority || "-",
        c.scheduledDate ? formatDateShort(c.scheduledDate) : "-",
      ]);
      addTable(
        [["Apartment", "Assigned To", "Status", "Priority", "Date"]],
        cleanBody
      );
    }

    const finRecords = cleanerView
      ? finSorter.sortedItems.filter((f: any) => f.type === "cleaning" && parseFloat(f.cleanerAmount || "0") > 0)
      : finSorter.sortedItems || [];
    if (finRecords.length > 0) {
      addSectionTitle(cleanerView ? "Earnings Detail" : ownerView ? "Charges Detail" : "Transaction Details");
      if (cleanerView) {
        const showCleanerCol = cleanerFilter === "all";
        const finBody = finRecords.map((f: any) => {
          const row = [
            f.apartmentName || apartmentMap[f.apartmentId] || "-",
            f.apartmentBedrooms === 0 ? "Studio" : `${f.apartmentBedrooms ?? 1}BR`,
          ];
          if (showCleanerCol) row.push(f.cleanerName || "-");
          row.push(formatMoney(f.cleanerAmount), f.status || "-", f.createdAt ? formatDateShort(f.createdAt) : "-");
          return row;
        });
        const headers = showCleanerCol
          ? [["Apartment", "BR", "Cleaner", "Earnings", "Status", "Date"]]
          : [["Apartment", "BR", "Earnings", "Status", "Date"]];
        addTable(headers, finBody, { [showCleanerCol ? 3 : 2]: { halign: "right" } });
      } else if (ownerView) {
        const finBody = finRecords.map((f: any) => [
          f.type === "check_in" ? "Check-in" : f.type === "unit_setup" ? "Unit Setup" : f.type === "maintenance" ? "Maintenance" : f.type === "miscellaneous" ? "Misc" : "Cleaning",
          f.apartmentName || apartmentMap[f.apartmentId] || "-",
          f.reservationGuestName || "-",
          formatMoney(f.totalAmount),
          f.status || "-",
          f.createdAt ? formatDateShort(f.createdAt) : "-",
        ]);
        addTable(
          [["Type", "Apartment", "Guest", "Amount", "Status", "Date"]],
          finBody,
          { 3: { halign: "right" } }
        );
      } else {
        const finBody = finRecords.map((f: any) => [
          f.type === "check_in" ? "Check-in" : f.type === "unit_setup" ? "Unit Setup" : f.type === "maintenance" ? "Maintenance" : f.type === "miscellaneous" ? "Misc" : "Cleaning",
          f.apartmentName || apartmentMap[f.apartmentId] || "-",
          f.reservationGuestName || "-",
          formatMoney(f.totalAmount),
          formatMoney(f.adminAmount),
          formatMoney(f.cleanerAmount),
          formatMoney(f.partnerAmount),
          f.status || "-",
          f.paymentMethod ? f.paymentMethod.replace(/_/g, " ") : "-",
          f.createdAt ? formatDateShort(f.createdAt) : "-",
        ]);
        addTable(
          [["Type", "Apartment", "Guest", "Total", "Admin", "Cleaner", "Partner", "Status", "Payment", "Date"]],
          finBody,
          { 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } }
        );
      }

      const totalLabel = cleanerView ? "Total Earnings" : "Total Amount";
      const totalValue = cleanerView
        ? formatMoney(finRecords.reduce((s: number, f: any) => s + Number(f.cleanerAmount || 0), 0))
        : formatMoney(finRecords.reduce((s: number, f: any) => s + Number(f.totalAmount || 0), 0));
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkText);
      doc.text(`${totalLabel}: ${totalValue}  |  Records: ${finRecords.length}`, margin, yPos);
      yPos += 8;
    }

    if (!ownerView && !cleanerView && (finData?.cleanerPayments || []).length > 0) {
      addSectionTitle("Cleaner Payment Summary");
      const cpBody = (finData.cleanerPayments || []).map((cp: any) => [
        cp.cleanerName,
        String(cp.jobsCompleted),
        formatMoney(cp.totalPay),
        formatMoney(cp.pendingPay),
        formatMoney(cp.paidPay),
      ]);
      addTable(
        [["Cleaner", "Jobs", "Total Owed", "Pending", "Paid"]],
        cpBody,
        { 1: { halign: "center" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } }
      );
    }

    if (!cleanerView && (expSorter.sortedItems || []).length > 0) {
      addSectionTitle("PM Expenses — Charged to Property Manager");
      const expBody = expSorter.sortedItems.map((e: any) => [
        e.apartmentName || "-",
        e.description || "-",
        e.category || "-",
        formatMoney(e.amount),
        e.marginPct ? `${e.marginPct}%` : "-",
        formatMoney(e.pmChargedAmount),
        e.status || "-",
        e.date ? formatDateShort(e.date) : "-",
      ]);
      addTable(
        [["Apartment", "Description", "Category", "Base", "Margin", "PM Charged", "Status", "Date"]],
        expBody,
        { 3: { halign: "right" }, 5: { halign: "right" }, 4: { halign: "center" } }
      );
    }

    if (!cleanerView) {
      const subRecords = subSorter.sortedItems || [];
      if (subRecords.length > 0) {
        addSectionTitle("Guest Checkout Submissions");
        const subBody = subRecords.map((s: any) => [
          s.guestName || "-",
          s.apartmentName || apartmentMap[s.apartmentId] || "-",
          s.guestPhone || "-",
          s.timestamp ? formatDateShort(s.timestamp) : "-",
        ]);
        addTable(
          [["Guest", "Apartment", "Phone", "Date"]],
          subBody
        );
      }
    }

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...mutedText);
      const pageH = doc.internal.pageSize.getHeight();
      doc.text(`Staio Report - Generated ${new Date().toLocaleDateString()}`, margin, pageH - 6);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageH - 6, { align: "right" });
    }

    const fileName = `staio-report-${startStr || "all"}-to-${endStr || "all"}.pdf`;
    doc.save(fileName);
  }

  const apartmentMap = useMemo(() => {
    const m: Record<number, string> = {};
    apartments.forEach((a: any) => { m[a.id] = a.name; });
    return m;
  }, [apartments]);

  const userMap = useMemo(() => {
    const m: Record<number, string> = {};
    allUsers.forEach((u: any) => { m[u.id] = u.fullName || u.username; });
    return m;
  }, [allUsers]);

  const resData = (reportData as any)?.reservations;
  const cleanData = (reportData as any)?.cleaning;
  const finData = (reportData as any)?.finances;
  const subData = (reportData as any)?.submissions;
  const expData = (reportData as any)?.pmExpenses;

  const resSorter = useSortableData(resData?.records || [], { key: "checkInDate", direction: "desc" });
  const cleanSorter = useSortableData(cleanData?.records || [], { key: "createdAt", direction: "desc" });
  const finSorter = useSortableData(finData?.records || [], { key: "createdAt", direction: "desc" });
  const revSorter = useSortableData(revData?.reservations || [], { key: "checkIn", direction: "desc" });
  const opCleanSorter = useSortableData(opsData?.cleaningJobs || [], { key: "scheduledDate", direction: "desc" });
  const subSorter = useSortableData(subData?.records || [], { key: "timestamp", direction: "desc" });
  const expSorter = useSortableData(expData?.records || [], { key: "date", direction: "desc" });

  return (
    <AdminLayout>
      <div className="space-y-6 print-report" ref={printRef}>
        <div className="flex items-start justify-between gap-4 flex-wrap no-print">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-reports-title">
              Reports
            </h1>
            <p className="text-muted-foreground">
              Generate detailed reports by date range
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch
                id="owner-view-toggle"
                checked={ownerView}
                onCheckedChange={(v) => { setOwnerView(v); if (v) setCleanerView(false); }}
                data-testid="switch-owner-view"
              />
              <Label htmlFor="owner-view-toggle" className="text-sm cursor-pointer flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                Property Manager View
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="cleaner-view-toggle"
                checked={cleanerView}
                onCheckedChange={(v) => { setCleanerView(v); if (v) setOwnerView(false); }}
                data-testid="switch-cleaner-view"
              />
              <Label htmlFor="cleaner-view-toggle" className="text-sm cursor-pointer flex items-center gap-1.5">
                <HardHat className="w-4 h-4" />
                Cleaner View
              </Label>
            </div>
            <Button
              variant="outline"
              onClick={() => repairMutation.mutate()}
              disabled={repairMutation.isPending}
              data-testid="button-sync-cleaner-records"
            >
              {repairMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync Cleaner Records
            </Button>
            <Button
              variant="outline"
              onClick={() => cleanupMutation.mutate()}
              disabled={cleanupMutation.isPending}
              data-testid="button-cleanup-records"
            >
              {cleanupMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clean Up Duplicates
            </Button>
            <Button variant="outline" onClick={generatePDF} data-testid="button-print-report">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        <ReportsFilterBar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          apartmentFilter={apartmentFilter}
          onApartmentChange={setApartmentFilter}
          apartments={apartments}
          ownerFilter={ownerFilter}
          onOwnerChange={setOwnerFilter}
          owners={owners}
          cleanerFilter={cleanerFilter}
          onCleanerChange={setCleanerFilter}
          cleaners={cleaners}
          showCleaner={!ownerView}
        />

        <div className="print-header hidden print:block mb-6">
          <h1 className="text-2xl font-bold">{ownerView ? "Property Manager Report - Staio" : "Staio Report"}</h1>
          <p className="text-muted-foreground">
            {dateRange.from && dateRange.to
              ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
              : "All dates"}
            {ownerFilter !== "all" && ` | Property Manager: ${userMap[Number(ownerFilter)] || ownerFilter}`}
            {apartmentFilter !== "all" && ` | Apartment: ${apartmentMap[Number(apartmentFilter)] || apartmentFilter}`}
            {!ownerView && cleanerFilter !== "all" && ` | Cleaner: ${userMap[Number(cleanerFilter)] || cleanerFilter}`}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : reportData ? (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="no-print flex-wrap">
              <TabsTrigger value="overview" data-testid="tab-overview">
                <BarChart3 className="w-4 h-4 mr-1" />
                Overview
              </TabsTrigger>
              {!cleanerView && (
                <TabsTrigger value="reservations" data-testid="tab-reservations">
                  <CalendarDays className="w-4 h-4 mr-1" />
                  Reservations
                </TabsTrigger>
              )}
              <TabsTrigger value="cleaning" data-testid="tab-cleaning">
                <Sparkles className="w-4 h-4 mr-1" />
                Cleaning
              </TabsTrigger>
              <TabsTrigger value="finances" data-testid="tab-finances">
                <DollarSign className="w-4 h-4 mr-1" />
                {cleanerView ? "Earnings" : "Finances"}
              </TabsTrigger>
              {!cleanerView && (
                <TabsTrigger value="submissions" data-testid="tab-submissions">
                  <ClipboardList className="w-4 h-4 mr-1" />
                  Submissions
                </TabsTrigger>
              )}
              {!cleanerView && !ownerView && (
                <>
                  <TabsTrigger value="operations" data-testid="tab-operations">
                    <Sparkles className="w-4 h-4 mr-1" />
                    Operations
                  </TabsTrigger>
                  <TabsTrigger value="revenue" data-testid="tab-revenue">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Revenue
                  </TabsTrigger>
                  <TabsTrigger value="sales" data-testid="tab-sales">
                    <Receipt className="w-4 h-4 mr-1" />
                    Sales
                  </TabsTrigger>
                </>
              )}
              {!cleanerView && !ownerView && (
                <TabsTrigger value="ai-report" data-testid="tab-ai-report">
                  <FileText className="w-4 h-4 mr-1" />
                  AI Report
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <MetricStrip cols={4}>
                {!cleanerView && (
                  <KpiCard
                    label="Reservations"
                    value={resData?.summary?.total || 0}
                    helper={`${resData?.summary?.checkedIn || 0} in · ${resData?.summary?.checkedOut || 0} out`}
                    icon={CalendarDays}
                    variant="default"
                    testId="stat-total-reservations"
                  />
                )}
                <KpiCard
                  label="Cleaning Jobs"
                  value={cleanData?.summary?.total || 0}
                  helper={`${cleanData?.summary?.completed || 0} done · ${cleanData?.summary?.pending || 0} pending`}
                  icon={Sparkles}
                  variant="info"
                  testId="stat-total-cleaning"
                />
                <KpiCard
                  label={cleanerView ? "Total Earnings" : ownerView ? "Total Charges" : "Total Revenue"}
                  value={cleanerView ? formatMoney(finData?.summary?.totalCleanerPayments) : formatMoney(finData?.summary?.totalRevenue)}
                  helper={cleanerView ? "from cleaning jobs" : `${formatMoney(finData?.summary?.paidAmount)} paid`}
                  icon={DollarSign}
                  variant="success"
                  testId="stat-total-revenue"
                />
                {!cleanerView && (
                  <KpiCard
                    label="Guest Checkouts"
                    value={subData?.summary?.total || 0}
                    helper="checkout submissions"
                    icon={ClipboardList}
                    variant="warning"
                    testId="stat-total-submissions"
                  />
                )}
              </MetricStrip>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{cleanerView ? "Earnings Breakdown" : ownerView ? "Charges Breakdown" : "Financial Breakdown"}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {!cleanerView && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Check-in Fees</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.checkInFees)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Cleaning Fees</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.cleaningFees)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Unit Setup Fees</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.unitSetupFees)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Maintenance</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.maintenanceFees)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Miscellaneous</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.miscFees)}</span>
                        </div>
                      </>
                    )}
                    {!ownerView && !cleanerView && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Admin Commission (gross)</span>
                          <span className="font-medium">{formatMoney(finData?.summary?.totalAdminCommission)}</span>
                        </div>
                        {parseFloat(finData?.summary?.totalOrgExpenses || "0") > 0 && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Org Expenses</span>
                              <span className="font-medium text-red-700 dark:text-red-400">−{formatMoney(finData?.summary?.totalOrgExpenses)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Net Profit</span>
                              <span className="font-bold text-emerald-700 dark:text-emerald-400">{formatMoney(finData?.summary?.netAdminProfit)}</span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Cleaner Payments</span>
                      <span className="font-medium">{formatMoney(finData?.summary?.totalCleanerPayments)}</span>
                    </div>
                    {!ownerView && !cleanerView && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{finData?.summary?.partnerName || "Partner"} ({finData?.summary?.partnerCommissionPct || "14"}%)</span>
                        <span className="font-medium">{formatMoney(finData?.summary?.totalPartnerPayments)}</span>
                      </div>
                    )}
                    {!cleanerView && parseFloat(finData?.summary?.totalSupplyCosts || "0") > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Supply Costs (consumed)</span>
                        <span className="font-medium text-amber-700 dark:text-amber-400">{formatMoney(finData?.summary?.totalSupplyCosts)}</span>
                      </div>
                    )}
                    {!cleanerView && parseFloat(expData?.summary?.totalPmCharged || "0") > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">PM Expenses ({expData?.summary?.total})</span>
                        <span className="font-medium text-blue-700 dark:text-blue-400">{formatMoney(expData?.summary?.totalPmCharged)}</span>
                      </div>
                    )}
                    {!cleanerView && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Pending Amount</span>
                          <span className="font-bold text-orange-600">{formatMoney(finData?.summary?.pendingAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Paid Amount</span>
                          <span className="font-bold text-green-600">{formatMoney(finData?.summary?.paidAmount)}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {!cleanerView && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Reservation Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Confirmed</span>
                        <span className="font-medium">{resData?.summary?.confirmed || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Checked In</span>
                        <span className="font-medium">{resData?.summary?.checkedIn || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Checked Out</span>
                        <span className="font-medium">{resData?.summary?.checkedOut || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Cancelled</span>
                        <span className="font-medium">{resData?.summary?.cancelled || 0}</span>
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Cleaning Urgent</span>
                        <span className="font-medium text-red-600 dark:text-red-400">{cleanData?.summary?.urgent || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Cleaning In Progress</span>
                        <span className="font-medium">{cleanData?.summary?.inProgress || 0}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {!ownerView && (finData?.cleanerPayments || []).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Cleaner Payments Due
                    </CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[400px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead>Cleaner</TableHead>
                              <TableHead className="text-center">Jobs</TableHead>
                              <TableHead className="text-right">Total Owed</TableHead>
                              <TableHead className="text-right">Pending</TableHead>
                              <TableHead className="text-right">Paid</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(finData?.cleanerPayments || []).map((cp: any, idx: number) => (
                              <TableRow key={cp.cleanerId} data-testid={`row-overview-cleaner-${cp.cleanerId}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{cp.cleanerName}</TableCell>
                                <TableCell className="text-center">{cp.jobsCompleted}</TableCell>
                                <TableCell className="text-right font-medium">{formatMoney(cp.totalPay)}</TableCell>
                                <TableCell className="text-right text-orange-600 dark:text-orange-400">{formatMoney(cp.pendingPay)}</TableCell>
                                <TableCell className="text-right text-green-600 dark:text-green-400">{formatMoney(cp.paidPay)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          {(finData?.cleanerPayments || []).length > 1 && (
                            <TableFooter>
                              <TableRow data-testid="row-overview-cleaner-totals">
                                <TableCell className="font-medium">Total</TableCell>
                                <TableCell className="text-center font-bold">{(finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.jobsCompleted || 0), 0)}</TableCell>
                                <TableCell className="text-right font-bold">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.totalPay || 0), 0))}</TableCell>
                                <TableCell className="text-right font-bold text-orange-600 dark:text-orange-400">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.pendingPay || 0), 0))}</TableCell>
                                <TableCell className="text-right font-bold text-green-600 dark:text-green-400">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.paidPay || 0), 0))}</TableCell>
                              </TableRow>
                            </TableFooter>
                          )}
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="reservations" className="space-y-4">
              <MetricStrip cols={4}>
                <KpiCard label="Total" value={resData?.summary?.total || 0} icon={CalendarDays} variant="default" />
                <KpiCard label="Confirmed" value={resData?.summary?.confirmed || 0} icon={CalendarDays} variant="info" />
                <KpiCard label="Checked In" value={resData?.summary?.checkedIn || 0} icon={CalendarDays} variant="success" />
                <KpiCard label="Checked Out" value={resData?.summary?.checkedOut || 0} icon={CalendarDays} variant="neutral" />
              </MetricStrip>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    Reservation Details
                  </CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-6">
                    <div className="min-w-[500px] px-6">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <SortableHead label="Guest" sortKey="guestName" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                            <SortableHead label="Apartment" sortKey="apartmentName" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                            <SortableHead label="Check-in" sortKey="checkInDate" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                            <SortableHead label="Check-out" sortKey="checkOutDate" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                            <SortableHead label="Status" sortKey="status" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                            <TableHead>Cleaning</TableHead>
                            <SortableHead label="Charges" sortKey="totalCharges" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} className="text-right" />
                            <SortableHead label="Source" sortKey="source" sortConfig={resSorter.sortConfig} onSort={resSorter.requestSort} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resSorter.sortedItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                                No reservations in this period
                              </TableCell>
                            </TableRow>
                          ) : (
                            resSorter.sortedItems.map((r: any, idx: number) => (
                              <TableRow key={r.id} data-testid={`row-reservation-${r.id}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{r.guestName}</TableCell>
                                <TableCell>{r.apartmentName || apartmentMap[r.apartmentId] || "-"}</TableCell>
                                <TableCell>{formatDateShort(r.checkInDate)}</TableCell>
                                <TableCell>{formatDateShort(r.checkOutDate)}</TableCell>
                                <TableCell>
                                  <Badge variant={statusColor(r.status)} className="text-xs">{r.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  {r.cleaningJobCount > 0 ? (
                                    <Badge variant={statusColor(r.cleaningStatus)} className="text-xs">
                                      {r.cleaningStatus}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">none</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {r.financialRecordCount > 0 ? (
                                    <span className="text-sm font-medium">{formatMoney(r.totalCharges)}</span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-muted-foreground">{r.source || "manual"}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                        {resSorter.sortedItems.length > 0 && (
                          <TableFooter>
                            <TableRow className="font-medium" data-testid="row-reservation-totals">
                              <TableCell colSpan={6}>
                                Total: {resSorter.sortedItems.length} reservations
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {formatMoney(resSorter.sortedItems.reduce((sum: number, r: any) => sum + Number(r.totalCharges || 0), 0))}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cleaning" className="space-y-4">
              <MetricStrip cols={5}>
                <KpiCard label="Total" value={cleanData?.summary?.total || 0} icon={Sparkles} variant="default" />
                <KpiCard label="Completed" value={cleanData?.summary?.completed || 0} icon={Sparkles} variant="success" />
                <KpiCard label="In Progress" value={cleanData?.summary?.inProgress || 0} icon={Sparkles} variant="info" />
                <KpiCard label="Pending" value={cleanData?.summary?.pending || 0} icon={Sparkles} variant="warning" />
                <KpiCard label="Urgent" value={cleanData?.summary?.urgent || 0} icon={Sparkles} variant="danger" />
              </MetricStrip>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    Cleaning Job Details
                  </CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-6">
                    <div className="min-w-[500px] px-6">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <SortableHead label="Apartment" sortKey="apartmentName" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            <SortableHead label="Guest" sortKey="reservationGuestName" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            <SortableHead label="Status" sortKey="status" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            <SortableHead label="Priority" sortKey="priority" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            {!ownerView && <SortableHead label="Assigned To" sortKey="assigneeName" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />}
                            <SortableHead label="Created" sortKey="createdAt" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            <SortableHead label="Completed" sortKey="completedAt" sortConfig={cleanSorter.sortConfig} onSort={cleanSorter.requestSort} />
                            <TableHead>Supply Cost</TableHead>
                            <TableHead>Billing</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cleanSorter.sortedItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={ownerView ? 8 : 9} className="text-center text-muted-foreground py-8">
                                No cleaning jobs in this period
                              </TableCell>
                            </TableRow>
                          ) : (
                            cleanSorter.sortedItems.map((j: any, idx: number) => (
                              <TableRow key={j.id} data-testid={`row-cleaning-${j.id}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{j.apartmentName || apartmentMap[j.apartmentId] || "-"}</TableCell>
                                <TableCell className="text-sm">
                                  {j.reservationGuestName || <span className="text-muted-foreground">-</span>}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={statusColor(j.status)} className="text-xs">{j.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant={j.priority === "urgent" ? "destructive" : "outline"} className="text-xs">{j.priority || "normal"}</Badge>
                                </TableCell>
                                {!ownerView && <TableCell>{j.assigneeName || (j.assignedTo ? userMap[j.assignedTo] : "-") || "-"}</TableCell>}
                                <TableCell>{j.createdAt ? formatDateShort(j.createdAt) : "-"}</TableCell>
                                <TableCell>{j.completedAt ? formatDateShort(j.completedAt) : "-"}</TableCell>
                                <TableCell>
                                  {parseFloat(j.supplyCost || "0") > 0 ? (
                                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400">{formatMoney(j.supplyCost)}</span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {j.financialRecordId ? (
                                    <div className="flex flex-col gap-0.5">
                                      <Badge variant={statusColor(j.financialStatus)} className="text-xs">{j.financialStatus}</Badge>
                                      <span className="text-xs text-muted-foreground">{formatMoney(j.financialAmount)}</span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                        {cleanSorter.sortedItems.length > 0 && (
                          <TableFooter>
                            <TableRow className="font-medium" data-testid="row-cleaning-totals">
                              <TableCell colSpan={ownerView ? 6 : 7}>
                                Total: {cleanSorter.sortedItems.length} jobs
                              </TableCell>
                              <TableCell className="font-bold text-amber-700 dark:text-amber-400">
                                {(() => {
                                  const total = cleanSorter.sortedItems.reduce((sum: number, j: any) => sum + Number(j.supplyCost || 0), 0);
                                  return total > 0 ? formatMoney(total) : "-";
                                })()}
                              </TableCell>
                              <TableCell className="font-bold">
                                {formatMoney(cleanSorter.sortedItems.reduce((sum: number, j: any) => sum + Number(j.financialAmount || 0), 0))}
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="finances" className="space-y-4">
              {cleanerView ? (
                <MetricStrip cols={2}>
                  <KpiCard label="Total Earnings" value={formatMoney(finData?.summary?.totalCleanerPayments)} icon={DollarSign} variant="success" />
                  <KpiCard label="Jobs Completed" value={cleanData?.summary?.completed || 0} icon={Sparkles} variant="info" />
                </MetricStrip>
              ) : (
                <MetricStrip cols={4}>
                  <KpiCard label={ownerView ? "Total Charges" : "Total Revenue"} value={formatMoney(finData?.summary?.totalRevenue)} icon={DollarSign} variant="success" />
                  <KpiCard label="Check-in Fees" value={formatMoney(finData?.summary?.checkInFees)} icon={Banknote} variant="info" />
                  <KpiCard label="Cleaning Fees" value={formatMoney(finData?.summary?.cleaningFees)} icon={Sparkles} variant="info" />
                  <KpiCard label="Unit Setup" value={formatMoney(finData?.summary?.unitSetupFees)} icon={FileText} variant="default" />
                  <KpiCard label="Maintenance" value={formatMoney(finData?.summary?.maintenanceFees)} icon={HardHat} variant="warning" />
                  <KpiCard label="Miscellaneous" value={formatMoney(finData?.summary?.miscFees)} icon={FileText} variant="neutral" />
                  {!ownerView && <KpiCard label="Admin Profit" value={formatMoney(finData?.summary?.totalAdminCommission)} icon={TrendingUp} variant="success" />}
                  {!ownerView && <KpiCard label="Cleaner Pay" value={formatMoney(finData?.summary?.totalCleanerPayments)} icon={Users} variant="default" />}
                  <KpiCard label="Pending" value={formatMoney(finData?.summary?.pendingAmount)} icon={ClipboardList} variant="warning" />
                </MetricStrip>
              )}

              {!ownerView && !cleanerView && (finData?.cleanerPayments || []).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Cleaner Payment Summary
                    </CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[400px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead>Cleaner</TableHead>
                              <TableHead className="text-center">Jobs</TableHead>
                              <TableHead className="text-right">Total Owed</TableHead>
                              <TableHead className="text-right">Pending</TableHead>
                              <TableHead className="text-right">Paid</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(finData?.cleanerPayments || []).map((cp: any, idx: number) => (
                              <TableRow key={cp.cleanerId} data-testid={`row-cleaner-pay-${cp.cleanerId}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{cp.cleanerName}</TableCell>
                                <TableCell className="text-center">{cp.jobsCompleted}</TableCell>
                                <TableCell className="text-right font-medium">{formatMoney(cp.totalPay)}</TableCell>
                                <TableCell className="text-right text-orange-600 dark:text-orange-400">{formatMoney(cp.pendingPay)}</TableCell>
                                <TableCell className="text-right text-green-600 dark:text-green-400">{formatMoney(cp.paidPay)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          {(finData?.cleanerPayments || []).length > 1 && (
                            <TableFooter>
                              <TableRow data-testid="row-cleaner-pay-totals">
                                <TableCell className="font-medium">Total</TableCell>
                                <TableCell className="text-center font-bold">{(finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.jobsCompleted || 0), 0)}</TableCell>
                                <TableCell className="text-right font-bold">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.totalPay || 0), 0))}</TableCell>
                                <TableCell className="text-right font-bold text-orange-600 dark:text-orange-400">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.pendingPay || 0), 0))}</TableCell>
                                <TableCell className="text-right font-bold text-green-600 dark:text-green-400">{formatMoney((finData?.cleanerPayments || []).reduce((s: number, cp: any) => s + Number(cp.paidPay || 0), 0))}</TableCell>
                              </TableRow>
                            </TableFooter>
                          )}
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    {cleanerView ? "Earnings Detail" : ownerView ? "Charges Detail" : "Transaction Details"}
                  </CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-6">
                    <div className="min-w-[500px] px-6">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            {!cleanerView && <SortableHead label="Type" sortKey="type" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />}
                            <SortableHead label="Apartment" sortKey="apartmentName" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />
                            <TableHead>BR</TableHead>
                            {cleanerView && cleanerFilter === "all" && <SortableHead label="Cleaner" sortKey="cleanerName" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />}
                            {!cleanerView && <SortableHead label="Guest" sortKey="reservationGuestName" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />}
                            <SortableHead label={cleanerView ? "Earnings" : "Amount"} sortKey={cleanerView ? "cleanerAmount" : "totalAmount"} sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} className="text-right" />
                            {!ownerView && !cleanerView && <SortableHead label="Admin" sortKey="adminAmount" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} className="text-right" />}
                            {!ownerView && !cleanerView && <SortableHead label="Cleaner" sortKey="cleanerAmount" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} className="text-right" />}
                            {!ownerView && !cleanerView && <TableHead className="text-right">{finData?.summary?.partnerName || "Partner"}</TableHead>}
                            <SortableHead label="Status" sortKey="status" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />
                            {!ownerView && !cleanerView && <TableHead>Payment</TableHead>}
                            {!cleanerView && <TableHead>Linked</TableHead>}
                            <SortableHead label="Date" sortKey="createdAt" sortConfig={finSorter.sortConfig} onSort={finSorter.requestSort} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(() => {
                            const records = cleanerView
                              ? finSorter.sortedItems.filter((f: any) => f.type === 'cleaning' && parseFloat(f.cleanerAmount || '0') > 0)
                              : finSorter.sortedItems;
                            if (records.length === 0) {
                              const colSpan = cleanerView ? (cleanerFilter === "all" ? 6 : 5) : ownerView ? 8 : 13;
                              return (
                                <TableRow>
                                  <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                                    {cleanerView ? "No cleaning earnings in this period" : "No financial records in this period"}
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return records.map((f: any, idx: number) => (
                              <TableRow key={f.id} data-testid={`row-finance-${f.id}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                {!cleanerView && (
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs">{f.type === "check_in" ? "Check-in" : f.type === "unit_setup" ? "Unit Setup" : f.type === "maintenance" ? "Maintenance" : f.type === "miscellaneous" ? "Misc" : "Cleaning"}</Badge>
                                  </TableCell>
                                )}
                                <TableCell className="font-medium">{f.apartmentName || apartmentMap[f.apartmentId] || "-"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{f.apartmentBedrooms === 0 ? "Studio" : `${f.apartmentBedrooms ?? 1}BR`}</TableCell>
                                {cleanerView && cleanerFilter === "all" && (
                                  <TableCell className="text-sm">{f.cleanerName || "-"}</TableCell>
                                )}
                                {!cleanerView && (
                                  <TableCell className="text-sm">
                                    {f.reservationGuestName || <span className="text-muted-foreground">-</span>}
                                  </TableCell>
                                )}
                                <TableCell className="text-right font-medium">{cleanerView ? formatMoney(f.cleanerAmount) : formatMoney(f.totalAmount)}</TableCell>
                                {!ownerView && !cleanerView && <TableCell className="text-right">{formatMoney(f.adminAmount)}</TableCell>}
                                {!ownerView && !cleanerView && <TableCell className="text-right">{formatMoney(f.cleanerAmount)}</TableCell>}
                                {!ownerView && !cleanerView && <TableCell className="text-right">{formatMoney(f.partnerAmount)}</TableCell>}
                                <TableCell>
                                  <Badge variant={statusColor(f.status)} className="text-xs">{f.status}</Badge>
                                </TableCell>
                                {!ownerView && !cleanerView && (
                                  <TableCell className="text-xs">
                                    {f.paymentMethod ? (
                                      <Badge variant="outline" className="text-xs">{f.paymentMethod.replace(/_/g, " ")}</Badge>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                )}
                                {!cleanerView && (
                                  <TableCell className="text-xs">
                                    <div className="flex flex-col gap-0.5">
                                      {f.linkedCleaningJobId && (
                                        <span className="text-muted-foreground">Job #{f.linkedCleaningJobId}</span>
                                      )}
                                      {f.linkedReservationId && (
                                        <span className="text-muted-foreground">Res #{f.linkedReservationId}</span>
                                      )}
                                      {!f.linkedCleaningJobId && !f.linkedReservationId && (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </div>
                                  </TableCell>
                                )}
                                <TableCell>{f.createdAt ? formatDateShort(f.createdAt) : "-"}</TableCell>
                              </TableRow>
                            ));
                          })()}
                        </TableBody>
                        {finSorter.sortedItems.length > 0 && (
                          <TableFooter>
                            <TableRow className="font-medium" data-testid="row-finance-totals">
                              <TableCell colSpan={cleanerView ? (cleanerFilter === "all" ? 3 : 2) : ownerView ? 4 : 4}>
                                Total: {cleanerView ? finSorter.sortedItems.filter((f: any) => f.type === 'cleaning' && parseFloat(f.cleanerAmount || '0') > 0).length : finSorter.sortedItems.length} records
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                {cleanerView
                                  ? formatMoney(finSorter.sortedItems.filter((f: any) => f.type === 'cleaning').reduce((s: number, f: any) => s + Number(f.cleanerAmount || 0), 0))
                                  : formatMoney(finSorter.sortedItems.reduce((s: number, f: any) => s + Number(f.totalAmount || 0), 0))
                                }
                              </TableCell>
                              {!ownerView && !cleanerView && <TableCell className="text-right font-bold">{formatMoney(finSorter.sortedItems.reduce((s: number, f: any) => s + Number(f.adminAmount || 0), 0))}</TableCell>}
                              {!ownerView && !cleanerView && <TableCell className="text-right font-bold">{formatMoney(finSorter.sortedItems.reduce((s: number, f: any) => s + Number(f.cleanerAmount || 0), 0))}</TableCell>}
                              {!ownerView && !cleanerView && <TableCell className="text-right font-bold">{formatMoney(finSorter.sortedItems.reduce((s: number, f: any) => s + Number(f.partnerAmount || 0), 0))}</TableCell>}
                              <TableCell colSpan={cleanerView ? 2 : ownerView ? 3 : 4} />
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!cleanerView && (expData?.records || []).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <Banknote className="h-4 w-4 text-muted-foreground" />
                      PM Expenses — Charged to Property Manager
                    </CardTitle>
                    <Separator />
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 mb-4 flex-wrap">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Total charged: </span>
                        <span className="font-semibold text-blue-700 dark:text-blue-400">{formatMoney(expData?.summary?.totalPmCharged)}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Pending: </span>
                        <span className="font-semibold text-orange-600">{formatMoney(expData?.summary?.totalPending)}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Invoiced: </span>
                        <span className="font-semibold text-green-600">{formatMoney(expData?.summary?.totalInvoiced)}</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto -mx-6">
                      <div className="min-w-[500px] px-6">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <SortableHead label="Apartment" sortKey="apartmentName" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} />
                              <SortableHead label="Description" sortKey="description" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} />
                              <SortableHead label="Category" sortKey="category" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} />
                              <SortableHead label="Base" sortKey="amount" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} className="text-right" />
                              <TableHead className="text-center">Margin</TableHead>
                              <SortableHead label="PM Charged" sortKey="pmChargedAmount" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} className="text-right" />
                              <SortableHead label="Status" sortKey="status" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} />
                              <SortableHead label="Date" sortKey="date" sortConfig={expSorter.sortConfig} onSort={expSorter.requestSort} />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {expSorter.sortedItems.map((e: any, idx: number) => (
                              <TableRow key={e.id} data-testid={`row-pm-expense-${e.id}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{e.apartmentName}</TableCell>
                                <TableCell className="text-sm">{e.description || "-"}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs capitalize">{e.category}</Badge>
                                </TableCell>
                                <TableCell className="text-right">{formatMoney(e.amount)}</TableCell>
                                <TableCell className="text-center text-xs text-muted-foreground">
                                  {e.marginPct ? `${e.marginPct}%` : "-"}
                                </TableCell>
                                <TableCell className="text-right font-semibold text-blue-700 dark:text-blue-400">{formatMoney(e.pmChargedAmount)}</TableCell>
                                <TableCell>
                                  <Badge variant={e.status === "invoiced" ? "default" : "outline"} className="text-xs">{e.status}</Badge>
                                </TableCell>
                                <TableCell>{e.date ? formatDateShort(e.date) : "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          <TableFooter>
                            <TableRow data-testid="row-pm-expense-totals">
                              <TableCell colSpan={5} className="font-medium">
                                Total: {expSorter.sortedItems.length} expenses
                              </TableCell>
                              <TableCell className="text-right font-bold text-blue-700 dark:text-blue-400">
                                {formatMoney(expSorter.sortedItems.reduce((s: number, e: any) => s + Number(e.pmChargedAmount || 0), 0))}
                              </TableCell>
                              <TableCell colSpan={2} />
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="submissions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <ClipboardList className="h-4 w-4 text-muted-foreground" />
                    Guest Checkout Submissions ({subData?.summary?.total || 0})
                  </CardTitle>
                  <Separator />
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-6">
                    <div className="min-w-[400px] px-6">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <SortableHead label="Guest" sortKey="guestName" sortConfig={subSorter.sortConfig} onSort={subSorter.requestSort} />
                            <SortableHead label="Apartment" sortKey="apartmentName" sortConfig={subSorter.sortConfig} onSort={subSorter.requestSort} />
                            <TableHead>Phone</TableHead>
                            <SortableHead label="Date" sortKey="timestamp" sortConfig={subSorter.sortConfig} onSort={subSorter.requestSort} />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {subSorter.sortedItems.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                                No submissions in this period
                              </TableCell>
                            </TableRow>
                          ) : (
                            subSorter.sortedItems.map((s: any, idx: number) => (
                              <TableRow key={s.id} data-testid={`row-submission-${s.id}`} className={idx % 2 === 1 ? "bg-muted/5" : ""}>
                                <TableCell className="font-medium">{s.guestName}</TableCell>
                                <TableCell>{s.apartmentName || apartmentMap[s.apartmentId] || "-"}</TableCell>
                                <TableCell>{s.guestPhone || "-"}</TableCell>
                                <TableCell>{s.timestamp ? formatDateShort(s.timestamp) : "-"}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                        {subSorter.sortedItems.length > 0 && (
                          <TableFooter>
                            <TableRow data-testid="row-submission-totals">
                              <TableCell colSpan={4} className="font-medium">
                                Total: {subSorter.sortedItems.length} submissions
                              </TableCell>
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="operations" className="space-y-4">
              <OperationsTab
                opsData={opsData}
                apartmentMap={apartmentMap}
                userMap={userMap}
                opsStatusFilter={opsStatusFilter}
                onOpsStatusChange={setOpsStatusFilter}
              />
            </TabsContent>

            <TabsContent value="revenue" className="space-y-4">
              <RevenueTab
                revData={revData}
                apartmentMap={apartmentMap}
                userMap={userMap}
              />
            </TabsContent>

            <TabsContent value="sales" className="space-y-4">
              <SalesTab salesData={salesData} isLoading={salesLoading} />
            </TabsContent>

            <TabsContent value="ai-report" className="space-y-4">
              <AiInsightsTab />
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Select a date range to generate a report
            </CardContent>
          </Card>
        )}
      </div>

      <style>{`
        @page {
          margin: 0.5in;
          margin-top: 0.3in;
          margin-bottom: 0.3in;
        }
        @media print {
          .no-print { display: none !important; }
          .print-report { padding: 0 !important; }
          .print-report .hidden.print\\:block { display: block !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          [role="tabpanel"][data-state="inactive"] { display: block !important; }
          [role="tabpanel"] { margin-top: 1rem; }
        }
      `}</style>
    </AdminLayout>
  );
}
